
// src/hooks/use-follow-status.ts
"use client";

import { useState, useEffect, useCallback } from 'react';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp, Timestamp, increment, runTransaction } from 'firebase/firestore';
import { useAuth } from '@/context/auth-context';
import { useI18n } from '@/context/i18n-context';
import type { Character } from '@/lib/types';
import { parseFormattedNumber } from '@/lib/firebaseUtils';


export const DISPATCH_FOLLOW_UPDATE_EVENT = 'firestoreFollowsUpdated';

export function useFollowStatus(characterId: string | null | undefined) {
  const { user, openAuthModal } = useAuth();
  const { t } = useI18n();
  const [isFollowed, setIsFollowed] = useState(false);
  const [isPerformingToggle, setIsPerformingToggle] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const checkFollowStatus = useCallback(async () => {
    if (!db) {
      console.warn("useFollowStatus: Firestore (db) not available for checking status. Ensure Firebase is initialized and .env.local is correct.");
      setIsInitialLoading(false);
      setIsFollowed(false);
      return;
    }
    if (!user || !characterId) {
      setIsFollowed(false);
      setIsInitialLoading(false);
      return;
    }

    setIsInitialLoading(true);
    try {
      const userFollowsDocRef = doc(db, 'userFollows', user.uid);
      const docSnap = await getDoc(userFollowsDocRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setIsFollowed(data.followingCharacterIds?.includes(characterId) || false);
      } else {
        setIsFollowed(false);
      }
    } catch (error) {
      console.error("Error checking follow status:", error);
      setIsFollowed(false);
    } finally {
      setIsInitialLoading(false);
    }
  }, [user, characterId, t, db]);

  useEffect(() => {
    checkFollowStatus();

    const handleFollowsUpdate = () => {
      // This will re-run checkFollowStatus, setting isInitialLoading true then false.
      // The button spinner is tied to isPerformingToggle, so this won't cause spinner flicker.
      checkFollowStatus();
    };
    
    window.addEventListener(DISPATCH_FOLLOW_UPDATE_EVENT, handleFollowsUpdate);

    return () => {
      window.removeEventListener(DISPATCH_FOLLOW_UPDATE_EVENT, handleFollowsUpdate);
    };
  }, [checkFollowStatus]);

  const toggleFollow = useCallback(async () => {
    if (!user) {
      openAuthModal('login', window.location.pathname);
      return;
    }
    if (!characterId) {
        console.warn("toggleFollow called with no characterId");
        return;
    }
    if (!db) {
      console.error("Database connection error. Please try again later.");
      return;
    }

    setIsPerformingToggle(true);
    const userFollowsDocRef = doc(db, 'userFollows', user.uid);
    const characterDocRef = doc(db, 'characters', characterId); 

    try {
      if (isFollowed) { // UNFOLIOWING
        // Update the user's follow list
        await updateDoc(userFollowsDocRef, {
          followingCharacterIds: arrayRemove(characterId),
          lastUpdated: serverTimestamp() 
        });
        
        // Safely decrement the character's follower count
        await runTransaction(db, async (transaction) => {
            const charDoc = await transaction.get(characterDocRef);
            if (!charDoc.exists()) return;
            const currentFollowers = charDoc.data().followerCount;
            const newFollowers = Math.max(0, parseFormattedNumber(currentFollowers) - 1);
            transaction.update(characterDocRef, {
                followerCount: newFollowers,
                updatedAt: serverTimestamp()
            });
        });
        
        setIsFollowed(false);
      } else { // FOLLOWING
        const userFollowsSnap = await getDoc(userFollowsDocRef);
        if (userFollowsSnap.exists()) {
          await updateDoc(userFollowsDocRef, {
            followingCharacterIds: arrayUnion(characterId),
            lastUpdated: serverTimestamp()
          });
        } else {
          await setDoc(userFollowsDocRef, {
            followingCharacterIds: [characterId],
            userId: user.uid,
            lastUpdated: serverTimestamp()
          });
        }

        // Safely increment the character's follower count
        await runTransaction(db, async (transaction) => {
            const charDoc = await transaction.get(characterDocRef);
            if (!charDoc.exists()) return;
            const currentFollowers = charDoc.data().followerCount;
            const newFollowers = parseFormattedNumber(currentFollowers) + 1;
            transaction.update(characterDocRef, {
                followerCount: newFollowers,
                updatedAt: serverTimestamp()
            });
        });

        setIsFollowed(true);
      }
      
      window.dispatchEvent(new CustomEvent(DISPATCH_FOLLOW_UPDATE_EVENT));

    } catch (error: any) {
      console.error("Error toggling follow status:", error);
      // Re-check status to ensure UI consistency after error
      if(db && user){ // Check db and user again before trying to read
        const currentDocSnap = await getDoc(userFollowsDocRef);
        if (currentDocSnap.exists()) {
            setIsFollowed(currentDocSnap.data().followingCharacterIds?.includes(characterId) || false);
        } else {
            setIsFollowed(false);
        }
      }
    } finally {
      setIsPerformingToggle(false);
    }
  }, [user, characterId, isFollowed, openAuthModal, t, db]); 

  return { isFollowed, toggleFollow, isPerformingToggle, isInitialLoading };
}
